/**
 * Stiletto nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nail_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const stilettoNailDesignsPack: ToilePack = {
    slug: 'stiletto-nail-designs',
    category: 'beauty',
    name: 'Stiletto nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'wine', label: 'Deep wine', subject: 'long stiletto nails in a glossy deep wine red tapering to sharp points'},
        {key: 'chrome', label: 'Gold chrome', subject: 'medium stiletto nails in a mirror gold chrome'},
        {key: 'gems', label: 'Gem accents', subject: 'stiletto nails in black with a line of small crystals down the centre of the ring-finger nail'},
    ],
    styles: [
        {key: 'salon', label: 'Salon close-up', style: 'nail salon close-up in soft diffuse daylight from camera-left, a gentle shadow under the fingers, a bright airy balance, the sharp points seen clearly', palette: 'White and pale grey ground, the color carried on the nails', framing: 'the hand resting flat on white marble, fingertips toward the top of a vertical frame'},
        {key: 'night', label: 'Night out', style: 'evening lifestyle photo in warm amber light from camera-right, deep soft shadows, a low warm balance with glints on the glass', palette: 'Amber and dark wood around the hand, the nails gleaming', framing: 'the hand holding the stem of a cocktail glass on a dark bar counter, the background a soft blur of warm points'},
        {key: 'velvet', label: 'Black velvet', style: 'beauty editorial in hard light from above, crisp shadow falling into black, a dramatic dark balance with the nails the brightest point', palette: 'Deep black velvet ground, the nails bright against it', framing: 'the hand raised against draped black velvet filling the frame, the fingers slightly apart to show each point'},
    ],
    directions: ['for Halloween', 'red chrome', 'snake print'],
    directionHint: 'try: for Halloween',
    seo: {
        title: 'Stiletto Nail Designs: Deep Wine, Gold Chrome, Gems',
        metaDescription: 'Nine stiletto nail designs: a deep wine solid, a mirror gold chrome and black nails with a crystal line, in three settings.',
        h1: 'Stiletto nail designs: deep wine, gold chrome and gem accents',
        tagline: 'Nine stiletto sets: three dramatic designs, each shown in a salon close-up, on a night out and in black velvet.',
        intro: 'Stiletto nail designs for people who want the stiletto shape, long and filed to a point, for a dramatic look. The three designs are long stiletto nails in a glossy deep wine, medium stilettos in a mirror gold chrome, and stilettos in black with a line of small crystals down the ring-finger nail. Type a different color or gem and the same pointed shape carries it.',
        useCases: [
            'a wine red set for an evening',
            'a gold chrome for a festival',
            'a black set with crystals for a party',
            'a pointed shape for a bold look',
        ],
        faq: [
            {q: 'What is a stiletto nail?', a: 'A long nail filed to a sharp point.'},
            {q: 'Are the gems on every nail?', a: 'No. The crystals run in a line down the center of the ring-finger nail only.'},
            {q: 'Can I ask for a different color?', a: 'Yes. A color you type is a request, not one of the nine pictures shown.'},
        ],
    },
}
